import { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from '@backstage/core-plugin-api';

/**
 * Sidebar analytics shared by the legacy sidebar and the BUI sidebar, so GA4
 * can compare them. Every event carries `variant`; see docs/portal-analytics.md
 * for the events and how to read them.
 */
export type SidebarVariant = 'legacy' | 'v2';
export type SidebarMode = 'expanded' | 'narrow';
export type SidebarNavigationSource =
  | 'favourites'
  | 'flyout'
  | 'more'
  | 'footer';

/** Path only: query strings (catalog filters) never reach GA4. */
export const sidebarEventPath = (href: string) => {
  try {
    return new URL(href, window.location.origin).pathname;
  } catch {
    return href.split(/[?#]/)[0];
  }
};

/** One `sidebar_view` per page load, so GA4 can segment users by variant. */
export const useSidebarViewEvent = (
  variant: SidebarVariant,
  mode: SidebarMode,
) => {
  const analytics = useAnalytics();
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    analytics.captureEvent('sidebar_view', variant, {
      attributes: { variant, mode },
    });
  }, [analytics, variant, mode]);
};

export const useSidebarAnalytics = (
  variant: SidebarVariant,
  mode: SidebarMode,
) => {
  const analytics = useAnalytics();
  const capture = useCallback(
    (
      action: string,
      subject: string,
      attributes: Record<string, string | number | boolean> = {},
    ) =>
      analytics.captureEvent(action, subject, {
        attributes: { variant, mode, ...attributes },
      }),
    [analytics, variant, mode],
  );

  return {
    capture,
    navigate: (to: string, source: SidebarNavigationSource) =>
      capture('sidebar_navigate', sidebarEventPath(to), { source }),
  };
};

/**
 * The legacy sidebar renders upstream SidebarItems without click analytics.
 * Its links are recognised by where they sit: the More menu, a group flyout
 * or the pinned list.
 */
export const legacySidebarNavigationSource = (
  link: Element,
): SidebarNavigationSource => {
  if (link.closest('#sidebar-more-menu')) return 'more';
  if (link.closest('[role="menu"]')) return 'flyout';
  if (link.closest('a[href="/settings"]')) return 'footer';
  return 'favourites';
};

export const useLegacySidebarNavigationAnalytics = (mode: SidebarMode) => {
  const { navigate } = useSidebarAnalytics('legacy', mode);
  return (event: MouseEvent<HTMLElement>) => {
    const link = (event.target as Element).closest?.('a[href]');
    if (!link) return;
    navigate(
      link.getAttribute('href') ?? '',
      legacySidebarNavigationSource(link),
    );
  };
};
